import { generatePath } from 'react-router-dom';
import { CATEGORIES, parameterize } from '@moda/portal-stanchions';

export type Params = { [param: string]: string | undefined };
export type GenerateHref = (params?: Params, parameterize?: boolean) => string;

const filterUndefinedParams = (params: Params): { [param: string]: string } => {
  return Object.fromEntries(Object.entries(params).filter(([, value]) => value !== undefined)) as {
    [param: string]: string;
  };
};

const isValidCategory = (category: string) => {
  return (
    !category ||
    Object.values(CATEGORIES)
      .map(cat => cat.toLowerCase())
      .includes(category.toLowerCase())
  );
};

const generateHrefFromSinglePattern = (
  pattern: string,
  params: Params = {},
  shouldParameterize = true
) =>
  shouldParameterize
    ? generatePath(
        pattern,
        Object.entries(filterUndefinedParams(params)).reduce(
          (acc, [key, value]) => ({ ...acc, [key]: value && parameterize(value) }),
          {}
        )
      )
    : generatePath(pattern, filterUndefinedParams(params));

const canGenerateHrefFromSinglePattern = (
  pattern: string,
  params: Params = {},
  shouldParameterize = true
) => {
  try {
    generateHrefFromSinglePattern(pattern, params, shouldParameterize);
    return true;
  } catch {
    return false;
  }
};

export const safeGenerateHref = (
  pattern: string | string[],
  params: Params = {},
  shouldParameterize = true
) => {
  if (params?.category && !isValidCategory(params?.category)) {
    params = { ...params, category: undefined };
  }
  return generateHref(pattern)(params, shouldParameterize);
};

export const generateHref =
  (pattern: string | string[]): GenerateHref =>
  (params = {}, shouldParameterize = true) => {
    const patterns = Array.isArray(pattern) ? pattern : [pattern];
    const lastValidPattern = [...patterns]
      .reverse()
      .find(pattern => canGenerateHrefFromSinglePattern(pattern, params, shouldParameterize));

    return generateHrefFromSinglePattern(
      lastValidPattern || patterns[0] || '',
      params,
      shouldParameterize
    );
  };
